import React, { useState } from "react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  Menu,
  Stack,
  CircularProgress,
} from "@mui/material";
import { DeleteOutline, ExpandMore } from "@mui/icons-material";
import { LAYOUT_HEIGHT } from "state/constants";
import api from "state/axios";
import DataGridComponent from "components/DataGridComponent";
import { 
  REQUEST_TYPE_OPTIONS, 
  STATUS_OPTIONS, 
  STATUS_COLOR, 
} from "./constants";


function StatusMenu({ row, onUpdated }) {
  const [anchor, setAnchor]   = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = async (newStatus) => {
    setAnchor(null);
    if (newStatus === row.status) return;
    setLoading(true);
    try {
      await api.patch(`/datarequests/status/${row._id}`, { status: newStatus });
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.message || "Update status failed!");
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Chip
        label={row.status}
        size="small"
        color={STATUS_COLOR[row.status] || "default"}
        onClick={(e) => setAnchor(e.currentTarget)}
        onDelete={(e) => setAnchor(e.currentTarget)}
        deleteIcon={loading ? <CircularProgress size={12} /> : <ExpandMore />}
        sx={{ 
          cursor: "pointer", 
          minWidth: 90, 
          justifyContent: "space-between" 
        }}
      />
      <Menu 
        anchorEl={anchor} 
        open={Boolean(anchor)} 
        onClose={() => setAnchor(null)}
      >
        {STATUS_OPTIONS?.map((s) => (
          <MenuItem 
            key={s} 
            selected={s === row.status} 
            onClick={() => handleChange(s)}
          >
            <Chip 
              label={s} 
              size="small" 
              color={STATUS_COLOR[s] || "default"} 
              sx={{ 
                pointerEvents: "none", 
                minWidth: 80, 
                justifyContent: "space-between" 
              }} 
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function RequestList({
  rows,
  loading,
  rowSelectionModel,
  setRowSelectionModel,
  columnVisibilityModel,
  setColumnVisibilityModel,
  onDelete,
  onStatusUpdated,
}) {
  const pendingCount    = rows.filter((r) => r.status === "Pending").length;
  const processingCount = rows.filter((r) => r.status === "Processing").length;
  const doneCount       = rows.filter((r) => r.status === "Done").length;
  const failedCount     = rows.filter((r) => r.status === "Failed").length;

  const columns = [
    {
      field: "startDate",
      headerName: "StartDate",
      width: 90,
      valueFormatter: (value) => value ? format(new Date(value), "dd/MM/yyyy") : "-",
    },
    {
      field: "endDate",
      headerName: "EndDate",
      width: 90,
      valueFormatter: (value) => value ? format(new Date(value), "dd/MM/yyyy") : "-",
    },
    {
      field: "crewCode",
      headerName: "CrewCode",
      width: 60,
      renderCell: ({ value }) =>
        value
          ? <Typography variant="body2">{value}</Typography>
          : <Typography variant="caption" color="text.disabled">-</Typography>,
    },
    {
      field: "requestTypes",
      headerName: "RequestTypes",
      flex: 2,
      sortable: false,
      cellClassName: "rt-cell",
      renderCell: ({ value }) => (
        <Box 
          display="flex" 
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
          gap={0.5}
          sx={{ 
            py: 0.5, 
            width: "100%", 
            maxHeight: "100%",
            overflowX: "hidden", 
            overflowY: "auto", 
          }}
        >
          {value?.map((t) => {
            const label = REQUEST_TYPE_OPTIONS?.find((r) => r.value === t)?.label || t;
            return (
              <Typography
                key={t}
                variant="caption"
                sx={{ width: "100%" }}
              >
                {label}
              </Typography>
            );
          })}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <StatusMenu row={row} onUpdated={onStatusUpdated} />
        </Box>
      ),
    },
    {
      field: "createdBy",
      headerName: "CreatedBy",
      width: 150,
      valueGetter: (value) =>
        value ? `${value.firstName || ""} ${value.lastName || ""}`.trim() : "-",
    },
    {
      field: "createdAt",
      headerName: "CreatedAt",
      width: 100,
      valueFormatter: (value) => value ? format(new Date(value), "dd/MM HH:mm") : "-",
    },
    {
      field: "actions",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Delete">
          <IconButton 
            size="small" 
            color="error" 
            onClick={() => onDelete([row._id])}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  
  return (
    <Card 
      elevation={3} 
      sx={{ 
        height: { xs: 'auto', md: LAYOUT_HEIGHT },
        display: "flex", 
        flexDirection: "column",
      }}
    >
      <CardContent 
        sx={{ 
          flex: 1, 
          minHeight: 0,
          display: "flex", 
          flexDirection: "column",
        }}
      >
        <Stack 
          direction="row" 
          flexWrap="wrap"
          alignItems="center" 
          spacing={1} 
          mb={2}
        >
          <Typography variant="h6" fontWeight={600}>
            Requests
          </Typography>
          <Chip 
            size="small" 
            label={`${pendingCount} Pending`}       
            color="warning" 
            variant="outlined" 
          />
          <Chip 
            size="small" 
            label={`${processingCount} Processing`} 
            color="info"    
            variant="outlined" 
          />
          <Chip 
            size="small" 
            label={`${doneCount} Done`}             
            color="success" 
            variant="outlined" 
          />
          {failedCount > 0 && (
            <Chip 
              size="small" 
              label={`${failedCount} Failed`} 
              color="error" 
              variant="outlined" 
            />
          )}
        </Stack>

        <DataGridComponent
          rows={rows}
          columns={columns}
          loading={loading}
          columnVisibilityModel={columnVisibilityModel}
          setColumnVisibilityModel={setColumnVisibilityModel}
          props={{
            checkboxSelection: true,
            disableRowSelectionOnClick: true,
            onRowSelectionModelChange: setRowSelectionModel,
            rowSelectionModel,
            rowHeight: 60,
            initialState: { pagination: { paginationModel: { pageSize: 50 } } },
          }}
        />
      </CardContent>
    </Card>
  );
}
